import _get from 'lodash/get'
import * as z from 'zod'

import {
  v2ContinueFooter,
  v2PetPortrait,
  v2Text,
  v2TextWithPetName,
} from '@flows/configs/petHealthV2/steps/elements/shared'
import { submitHealthStatus } from '@flows/configs/petHealthV2/utils'
import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import type { StepConfigParams } from '@flows/configs/types'
import type { Condition } from '@flows/core/types/stepContentTypes'
import type { AnyStepConfiguration, FlowValues } from '@flows/core/types/stepTypes'
import { BaseSchema_PetHealthV2GetsafeDe } from '@flows/insurance/schemas'

const HEALTH_STATUS = BaseSchema_PetHealthV2GetsafeDe.shape.current_health_status.unwrap()
const COMPLAINT_STATUSES = HEALTH_STATUS.options.filter((status) => status !== 'healthy')
const PAYMENT_ROUTING = {
  native: {
    onPrepare: {
      type: 'dispatchedHook',
      callbackConfig: {
        name: 'verifyPaymentNeeded',
        inputs: [],
        outputs: ['helperValues.hasPaymentData'],
      },
    },
    nextStepConfig: {
      valueFunction: (flowValues: FlowValues) =>
        String(_get(flowValues, 'helperValues.hasPaymentData') === true),
      valueToStepName: { false: 'StepPaymentMethod', true: 'pet_health_v2_consent' },
    },
  },
  web: { nextStep: 'payment' },
} as const

const current_health = (params: StepConfigParams, platform: 'native' | 'web') => {
  const { getParamPath, getTransientPath } = params.helperFunctions
  const statusPath = getParamPath('current_health_status')
  const acknowledgedPath = getTransientPath('health_status_acknowledged')
  const hasComplaints = {
    type: 'conditions',
    comparisonType: 'anyOf',
    conditions: COMPLAINT_STATUSES.map((status) => ({
      type: 'value',
      valuePath: statusPath,
      expectedValue: status,
    })),
  } as const satisfies Condition

  return {
    stepContent: [
      ...(platform === 'native' ? [v2PetPortrait(params)] : []),
      { type: 'title', text: v2TextWithPetName(params, 'steps.current_health.title') },
      {
        type: 'choice',
        shouldTrackValues: true,
        shouldAutoSubmit: false,
        variant: 'filled',
        name: statusPath,
        options: HEALTH_STATUS.options.map((value) => ({
          value,
          label: v2Text(`steps.current_health.options.${value}`),
        })),
      },
      {
        type: 'infoBox',
        showInfoIcon: true,
        contentConfiguration: [
          { type: 'text', text: v2TextWithPetName(params, 'steps.current_health.notice') },
        ],
        condition: hasComplaints,
      },
      {
        type: 'checkbox',
        noBackground: true,
        fields: [{ name: acknowledgedPath, title: v2Text('steps.current_health.acknowledgement') }],
        condition: hasComplaints,
      },
    ],
    schema: schemaUtils
      .generateZodObjectFromPaths({ [statusPath]: HEALTH_STATUS })
      .merge(
        schemaUtils
          .generateZodObjectFromPaths({ [acknowledgedPath]: z.boolean().optional() })
          .partial()
      )
      .superRefine((values, ctx) => {
        if (_get(values, statusPath) !== 'healthy' && _get(values, acknowledgedPath) !== true) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Required',
            path: acknowledgedPath.split('.'),
          })
        }
      }),
    onSubmit: {
      type: 'simpleHook',
      callback: (flowValues: FlowValues) => submitHealthStatus(params, flowValues),
    },
    subFlow: params.subFlow,
    ...PAYMENT_ROUTING[platform],
    ...v2ContinueFooter(platform),
  } as const satisfies AnyStepConfiguration
}

export default current_health
